import { useMemo, useState } from 'react'

import { Dialog, DialogOverlay, DialogContent } from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'

import { CardSchool } from 'app/KinerjaPage/components/CardSchool'
import { PENGELOLAAN_KINERJA_KS_PENILAI_PATH } from 'app/KinerjaShared/constants/path'
import { useApiGetPenilaianSchoolList } from 'api/kinerja/kepala-sekolah/school'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'
import CardPenilaianPeriodik from 'app/KinerjaShared/components/CardPenilaianPeriodik'
import { useApiGetPenilaianPeriodikType } from 'api/kinerja/kepala-sekolah/penilaian-periodik'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import useToggleSdkContext from 'utils/useToogleSdk'

interface PenilaianPeriodikCardProps {
  onCtaClick?: () => void
  onSchoolClick?: () => void
}

export function PenilaianPeriodikCard({
  onCtaClick,
  onSchoolClick,
}: PenilaianPeriodikCardProps) {
  const { tipe } = useKinerjaTipe()
  const { periodYear } = usePeriodContext()

  // ------------------
  // Dialog handler
  // ------------------
  const [isSchoolSelectionDialogOpen, setIsSchoolSelectionDialogOpen] =
    useState(false)

  const handleCloseSchoolSelectionDialog = () => {
    setIsSchoolSelectionDialogOpen(false)
  }

  const handleOpenSchoolSelectionDialog = () => {
    onCtaClick?.()
    setIsSchoolSelectionDialogOpen(true)
  }

  // ------------------
  // Resolve toggle value
  // ------------------
  const toggleValue = useToggleSdkContext()
  const isPenilaianPeriodikEnabled =
    toggleValue?.['kinerja-penilaian-periodik-enabled'] && periodYear >= 2025

  // ------------------
  // Get penilaian periodik school list
  // ------------------
  const { data, isLoading: isSchoolListLoading } = useApiGetPenilaianSchoolList(
    {
      params: { tipe },
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
        enabled: isPenilaianPeriodikEnabled,
      },
    }
  )

  const schoolLength = data?.data?.length || 0
  const ctaText = schoolLength > 1 ? 'Lakukan Penilaian' : 'Pilih'

  // Only show negeri schools
  const negeriSchools = useMemo(
    () =>
      data?.data?.filter((school) => {
        const isNegeri = school.statusSatuanPendidikan === 'NEGERI'
        const isSpnf =
          school.role === 'KS_PLT_SPNF' || school.role === 'KS_DEFINITIF_SPNF'
        return isNegeri && !isSpnf
      }) || [],
    [data]
  )

  // ------------------
  // Get penilaian periodik type
  // ------------------
  const { data: periodicTypeData, isLoading: isPeriodicTypeLoading } =
    useApiGetPenilaianPeriodikType({
      params: { skpYear: periodYear, tipe },
      options: {
        enabled: isPenilaianPeriodikEnabled,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  if (isPeriodicTypeLoading || isSchoolListLoading) {
    return <Shimmer className="!h-36 !w-full block rounded-lg" />
  }

  if (
    negeriSchools.length === 0 ||
    periodicTypeData.data.periodicType === 'NOT_FOUND'
  ) {
    return null
  }

  return (
    <>
      <CardPenilaianPeriodik
        periodicType={periodicTypeData.data.periodicType}
        onCtaClick={handleOpenSchoolSelectionDialog}
      />

      <Dialog
        isOpen={isSchoolSelectionDialogOpen}
        onClose={handleCloseSchoolSelectionDialog}
        customSize={596}
        autoFocus={false}
      >
        <DialogOverlay />
        <DialogContent
          className="rounded-lg m-auto self-center"
          aria-labelledby="periodik-pilih-satdik-title"
        >
          <div className="rounded-t-lg flex p-6 justify-between items-center bg-surface-subdued">
            <Text as="h2" variant="heading-md" id="periodik-pilih-satdik-title">
              Pilih satuan pendidikan
            </Text>
            <Button
              color="white"
              iconOnly
              size="sm"
              variant="ghost"
              className="!outline-none"
              onClick={handleCloseSchoolSelectionDialog}
            >
              <Icon color="inherit">close</Icon>
            </Button>
          </div>
          <div className="flex py-4 px-6 flex-col gap-4 max-h-[640px] overflow-y-auto">
            {negeriSchools.map((school) => (
              <CardSchool
                fullWidth
                key={school.npsn}
                ctaText={ctaText}
                ctaLabel={`${ctaText} ${school.schoolName}`}
                baseHref={`${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/periodik`}
                school={{
                  ...school,
                  perencanaanReadyState: 'COMPLETED',
                }}
                onClick={onSchoolClick}
              />
            ))}
          </div>

          <div className="p-6">
            <Button
              fullWidth
              color="white"
              onClick={handleCloseSchoolSelectionDialog}
            >
              Kembali
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
